import { Row, Stack } from "@justworkshr/milo-core";
import { DisplayIcon, DisplayIconNames } from "@justworkshr/milo-icons";
import { ReactElement } from "react";
import styles from "../../InternationalSolutionsPage.module.css";
import { CF_INTERNATIONAL_SOLUTIONS_CONTRACTORS_LINK } from "../../constants";

type FeatureCardProps = {
  iconName: keyof typeof DisplayIconNames;
  iconTitle: string;
  title: string;
  description: string;
};

const FeatureCard = ({
  iconName,
  iconTitle,
  title,
  description,
}: FeatureCardProps) => {
  const { smHead, featureCardRow, featureCardIcon, mdFont } = styles;

  return (
    <Row spacing="lg" className={featureCardRow}>
      <DisplayIcon
        color="greenYellow"
        iconName={iconName}
        size="small"
        title={iconTitle}
        className={featureCardIcon}
      />
      <Stack spacing="xs">
        <h4 className={smHead}>{title}</h4>
        <p className={mdFont}>{description}</p>
      </Stack>
    </Row>
  );
};

const Overview = (): ReactElement => {
  const { lgHead, mdFont, xlHeadAlt } = styles;

  return (
    <Stack spacing="2xl">
      <Stack spacing="sm">
        <p className={xlHeadAlt}>Employer of Record (EOR)</p>
        <p className={mdFont}>
          Justworks EOR makes global hiring simple and scalable for your
          business. As the Employer of Record, we handle all the legal, tax, and
          HR responsibilities of employing workers in other countries. Looking
          for shorter-term workers?{" "}
          <a href={CF_INTERNATIONAL_SOLUTIONS_CONTRACTORS_LINK}>
            Try international contractors
          </a>
        </p>
      </Stack>
      <Stack spacing="md">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="31"
          height="27"
          viewBox="0 0 31 27"
          fill="none"
        >
          <path
            d="M11.4041 0L7.22798 12.9341H13.9741V27H0V12.9341L6.58549 0H11.4041ZM28.4301 0L24.2539 12.9341H31V27H17.0259V12.9341L23.6114 0H28.4301Z"
            fill="#0070D4"
          />
        </svg>
        <Stack spacing="md" className={mdFont}>
          <p>
            Justworks adds tremendous value by managing all of the nuances and
            complexities of employing people across twenty states and multiple
            countries.
          </p>
          <span>
            — <b>Jeremy Clent</b>, Co-Founder, Project Expedition
          </span>
        </Stack>
      </Stack>
      <Stack spacing="sm">
        <p className={lgHead}>Support talent, wherever you find it</p>
        <FeatureCard
          iconName="shield"
          iconTitle="Shield Icon"
          title="Support talent, wherever you find it"
          description="Focus on building your business, and leave local labor laws, statutory leave, tax regulations, and employment agreements to us."
        />
        <FeatureCard
          iconName="circle-with-cross"
          iconTitle="Circle with Cross Icon"
          title="Competitive local benefits"
          description="Attract and retain top talent with country-specific benefits packages."
        />
        <FeatureCard
          iconName="hand-waving"
          iconTitle="Hand Waving Icon"
          title="Expert, in-house support"
          description="Get answers fast with 24-hour response times from our award-winning team."
        />
        <FeatureCard
          iconName="laptop-and-plus-sign"
          iconTitle="Laptop and Plus Sign Icon"
          title="One single, trusted platform"
          description="Manage your whole team with Justworks, including onboarding, time off policy management, time tracking, integrations, and more."
        />
      </Stack>
    </Stack>
  );
};

export default Overview;
